import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Plus, X, Armchair, AlertTriangle, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { PhotoUploader, FURNITURE_STAGES, FURNITURE_STAGE_LABELS, FURNITURE_STAGE_COLORS } from './FurnitureShared';

export const FurnitureList = ({ projectId }) => {
  const { authFetch, user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ descripcion: '', solicitante: '', fecha: new Date().toISOString().slice(0, 10), fotos: [] });
  const [saving, setSaving] = useState(false);
  const [pendingId, setPendingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await authFetch(`/api/furniture/?project_id=${projectId}`);
      if (res.ok) setItems(await res.json());
    } catch {}
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await authFetch('/api/furniture/', { method: 'POST', body: JSON.stringify({ ...form, project_id: projectId, fotos: [] }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success('Solicitud de mueble creada');
      setShowModal(false);
      setForm({ descripcion: '', solicitante: '', fecha: new Date().toISOString().slice(0, 10), fotos: [] });
      navigate(`/os/projects/${projectId}/furniture/${data.id}`);
    } catch (err) { toast.error(err.message || 'Error al crear'); }
    setSaving(false);
  };

  const canCreate = ['admin', 'designer', 'procurement'].includes(user?.role);

  return (
    <div data-testid="furniture-list">
      <div className="flex items-center justify-between mb-4">
        <h2 className="flex items-center gap-2 text-lg font-light" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D2D2D' }}>
          <Armchair size={18} style={{ color: '#C9A66B' }} /> Mobiliario a la Medida
        </h2>
        {canCreate && (
          <button data-testid="add-furniture-btn" onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-light tracking-wide transition-all duration-300"
            style={{ backgroundColor: '#2D2D2D', color: '#FAF9F6' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#C9A66B'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2D2D2D'}>
            <Plus size={14} /> Mueble
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="rounded-xl border p-10 text-center" style={{ backgroundColor: '#FFFFFF', borderColor: '#E8E3D8' }}>
          <p className="text-sm font-light" style={{ color: '#C9B8A8' }}>No hay solicitudes de mobiliario para este proyecto</p>
        </div>
      ) : (
        <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: '#FFFFFF', borderColor: '#E8E3D8' }}>
          {items.map((m, i) => {
            const color = FURNITURE_STAGE_COLORS[m.stage] || '#C9B8A8';
            const pct = Math.round(((m.stage_index + 1) / FURNITURE_STAGES.length) * 100);
            return (
              <button key={m.id} data-testid={`furniture-row-${m.id}`}
                onClick={() => navigate(`/os/projects/${projectId}/furniture/${m.id}`)}
                className={`w-full flex items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-neutral-50 ${i > 0 ? 'border-t' : ''}`}
                style={{ borderColor: '#E8E3D8' }}>
                <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} title={m.stage_label} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-light truncate" style={{ color: '#2D2D2D' }}>{m.nombre}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="w-28 h-1 rounded-full overflow-hidden" style={{ backgroundColor: '#F5F3EF' }}>
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
                    </div>
                    <span className="text-[10px]" style={{ color: '#C9B8A8' }}>{m.stage_index + 1}/9 · {m.stage_label}</span>
                  </div>
                </div>
                {m.alert && (
                  <span data-testid={`furniture-alert-${m.id}`} className="hidden sm:flex items-center gap-1 text-[10px] px-2.5 py-1 rounded-full shrink-0" style={{ backgroundColor: '#B87B7B15', color: '#B87B7B' }}>
                    <AlertTriangle size={10} /> {m.alert}
                  </span>
                )}
                <ChevronRight size={15} style={{ color: '#C9B8A8' }} />
              </button>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="rounded-2xl border p-8 w-full max-w-md shadow-xl" style={{ backgroundColor: '#FFFFFF', borderColor: '#E8E3D8' }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-light" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D2D2D' }}>Nueva Solicitud de Mueble</h2>
              <button onClick={() => setShowModal(false)} style={{ color: '#C9B8A8' }}><X size={20} /></button>
            </div>
            <form onSubmit={create} className="space-y-4">
              <div>
                <label className="block text-xs font-medium tracking-wider uppercase mb-1.5" style={{ color: '#C9B8A8' }}>Descripción del mueble</label>
                <textarea data-testid="furniture-field-descripcion" value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} required rows={3}
                  className="w-full px-3 py-2.5 rounded-lg text-sm outline-none resize-none" style={{ backgroundColor: '#FAF9F6', border: '1px solid #E8E3D8', color: '#2D2D2D' }}
                  placeholder="Ej: Mueble de lavabo para baño principal con espacio para toallas..." />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium tracking-wider uppercase mb-1.5" style={{ color: '#C9B8A8' }}>Quién lo solicita</label>
                  <input data-testid="furniture-field-solicitante" value={form.solicitante} onChange={e => setForm({ ...form, solicitante: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg text-sm outline-none" style={{ backgroundColor: '#FAF9F6', border: '1px solid #E8E3D8', color: '#2D2D2D' }} placeholder="Nombre" />
                </div>
                <div>
                  <label className="block text-xs font-medium tracking-wider uppercase mb-1.5" style={{ color: '#C9B8A8' }}>Fecha</label>
                  <input data-testid="furniture-field-fecha" type="date" value={form.fecha} onChange={e => setForm({ ...form, fecha: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg text-sm outline-none" style={{ backgroundColor: '#FAF9F6', border: '1px solid #E8E3D8', color: '#2D2D2D' }} />
                </div>
              </div>
              <p className="text-[10px] font-light" style={{ color: '#C9B8A8' }}>Las fotos del espacio se agregan al abrir el mueble.</p>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2.5 rounded-lg text-sm border" style={{ borderColor: '#E8E3D8', color: '#2D2D2D' }}>Cancelar</button>
                <button data-testid="furniture-submit-btn" type="submit" disabled={saving} className="flex-1 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50" style={{ backgroundColor: '#2D2D2D', color: '#FAF9F6' }}>
                  {saving ? 'Creando...' : 'Crear solicitud'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
